import React, { FC, useState, useEffect } from 'react';

import { configureUserManager, getUserData, isAuthenticated } from './services/authService';

import { AppContextProps, Language, LanguageProps } from './models/app';
import { Role } from './models/auth';

const userManager = configureUserManager();

export const LanguageContext = React.createContext<LanguageProps>({
  language: 'de-DE',
  setLanguage: (language) => { },
});

export const defaultLanguageContext: LanguageProps = {
  language: 'de-DE',
  setLanguage: (language) => { }
};

export const AppContext = React.createContext<AppContextProps>({
  lang: 'en-US',
  authenticated: true,
  theme: 'light',
  userManager: userManager && userManager,
  role: null,
  userEmail: null,
  userDisplayName: null,
  sessionExpires: null,
  setLang: () => { },
  setUserRole: () => { },
  setUserDisplayName: () => { },
  setUserEmail: () => { },
  setSessionExpires: () => { }
});

export const defaultAppContext: AppContextProps = {
  lang: 'de-DE',
  authenticated: true,
  theme: 'light',
  userManager: userManager && userManager,
  role: null,
  userEmail: null,
  userDisplayName: null,
  sessionExpires: null,
  setLang: () => { },
  setUserRole: () => { },
  setUserDisplayName: () => { },
  setUserEmail: () => { },
  setSessionExpires: () => { }
};

export const AppContextProvider = ({ children }: any) => {
  const [role, setRole] = useState<Role>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [sessionExpires, setSessionExpires] = useState<number | null>(null);

  const getUser = (userData: string) => {
    if (!userData) return;
    const { role: roleData, displayName: displayNameData, email: userEmailData, sessionExpires: sessionExpires } = JSON.parse(
      userData
    );

    setRole(roleData);
    setDisplayName(displayNameData);
    setUserEmail(userEmailData);
    setSessionExpires(sessionExpires);
  };

  useEffect(() => {
    let isCanceled = false;

    const asyncFn = async () => {
      if (isAuthenticated(userManager)) {
        const userData = await getUserData(userManager);
        if (isCanceled) return;
        getUser(userData);
      }
    };
    asyncFn();

    return () => {
      isCanceled = true;
    };
  }, []);

  const provider: AppContextProps = {
    ...defaultAppContext,
    role,
    userDisplayName: displayName,
    userEmail: userEmail,
    sessionExpires
  };

  return <AppContext.Provider value={provider}>{children}</AppContext.Provider>;
};

export const LanguageContextProvider = ({ children }: any) => {
  const [language, setLanguage] = useState<Language>('de-DE');

  const initializeLanguage = async () => {

    let lang = localStorage.getItem('language');
    if (lang) {
      if (lang === 'en-US') {
        setLanguage('en-US');
      } else {
        setLanguage('de-DE');
      }
    }
  };

  useEffect(() => {
    initializeLanguage();
  }, []);

  const setLang = (language: Language) => {
    localStorage.setItem('language', language);
    setLanguage(language);
  };

  const provider: LanguageProps = {
    ...defaultLanguageContext,
    language,
    setLanguage: (language: Language) => setLang(language)
  };

  return <LanguageContext.Provider value={provider}>{children}</LanguageContext.Provider>;
};

export const AppContextConsumer = AppContext.Consumer;

export function WithContext<
  P extends { appContext?: AppContextProps },
  R = Omit<P, 'appContext'>
>(Component: React.StatelessComponent<P> | FC<P>): FC<R> {
  return function BoundComponent(props: R) {
    return (
      <AppContextConsumer>
        {(value) => (
          <Component {...((props as unknown) as P)} appContext={value} />
        )}
      </AppContextConsumer>
    );
  };
}
